"use client";

import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Clock,
  Loader2,
  RefreshCw,
  ServerCrash,
  WifiOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { isAuthErrorLike, signOutAndGoToSignIn } from "@/lib/clientAuthError";
import {
  canAutoReload,
  isReloadRecoverableError,
  performAutoReload,
} from "@/lib/recoverableError";
import { classifyRuntimeError } from "@/lib/runtimeError";

// Shared error UI for every route error boundary across the app. Each
// `error.tsx` (root, group, or per-route) is a thin wrapper that renders this
// with a `source` label. Keeps behaviour identical everywhere:
//  - transient errors a fresh load fixes are auto-reloaded (once per path/min);
//  - auth errors sign the user out;
//  - everything else shows a friendly, brand-themed card (never a raw message);
//  - every error is reported to /api/report-error for observability.
export default function RouteErrorBoundary({
  error,
  reset,
  source = "route.error.boundary",
}: {
  error: Error & { digest?: string };
  reset: () => void;
  source?: string;
}) {
  const { isTimeout, isNetwork, isServerError } = classifyRuntimeError(error);

  // Stale-chunk/deploy, hydration, stale server-action errors are fixed by one
  // clean reload — do it automatically instead of showing the card.
  const [autoReloading, setAutoReloading] = useState(
    () => isReloadRecoverableError(error) && canAutoReload(),
  );

  // Human-readable copy only — never surface raw JS/API error text.
  const ui = useMemo(() => {
    if (isTimeout) {
      return {
        Icon: Clock,
        title: "The request took too long",
        description:
          "Our servers are taking longer than expected. Please try again in a moment.",
      };
    }
    if (isServerError) {
      return {
        Icon: ServerCrash,
        title: "We couldn’t reach the server",
        description:
          "The server didn’t respond. Please try again — it’s usually temporary.",
      };
    }
    if (isNetwork) {
      return {
        Icon: WifiOff,
        title: "Connection problem",
        description: "Please check your internet connection and try again.",
      };
    }
    return {
      Icon: AlertTriangle,
      title: "Something went wrong",
      description:
        "An unexpected error occurred on this page. Try again — if it keeps happening, the team has already been notified.",
    };
  }, [isTimeout, isNetwork, isServerError]);

  useEffect(() => {
    if (isAuthErrorLike(error)) {
      void signOutAndGoToSignIn();
      return;
    }
    // Report every error (including auto-recovered ones) for observability.
    try {
      fetch("/api/report-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source,
          name: error?.name,
          message: error?.message,
          stack: error?.stack,
          digest: (error as any)?.digest,
          isTimeout,
          isNetwork,
          isServerError,
          autoReloaded: autoReloading,
          path:
            typeof window !== "undefined"
              ? window.location.pathname
              : undefined,
        }),
        keepalive: true,
      });
    } catch {}
    if (autoReloading && !performAutoReload()) {
      // Guard stamp failed to write — reload never started. Show the card
      // instead of leaving the user on a spinner that never resolves.
      setAutoReloading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (autoReloading) {
    return (
      <div className="flex min-h-[60vh] w-full items-center justify-center p-6">
        <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-6 py-4 text-sm text-muted-foreground shadow-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Refreshing the page…
        </div>
      </div>
    );
  }

  const { Icon } = ui;

  return (
    <div className="flex min-h-[60vh] w-full items-center justify-center p-6">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-500/10">
          <Icon className="h-6 w-6 text-rose-500" />
        </div>
        <h2 className="mt-4 text-xl font-semibold text-foreground">
          {ui.title}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {ui.description}
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button variant="outline" onClick={() => reset()}>
            Try again
          </Button>
          <Button onClick={() => window.location.reload()} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh page
          </Button>
        </div>
        {error?.digest ? (
          <p className="mt-5 text-xs text-muted-foreground">
            Reference code:{" "}
            <span className="font-mono font-medium text-foreground/70">
              {error.digest}
            </span>
          </p>
        ) : null}
      </div>
    </div>
  );
}
